import { CURRENCIES } from '@/modules/currencies';
import { roundPrice } from '@/utils';
import { countryLocale } from './store-country';

/** Largest price the API accepts (decimal(10,2)). */
export const MAX_PRICE = 99999999.99;

/**
 * «pesos argentinos», «euros»: the plural currency name in the locale;
 * fallback (no `formatToParts` on Safari 12, unknown code): the code.
 */
export function currencyPluralName(code: string, locale: string): string {
  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'name',
    });
    if (typeof formatter.formatToParts !== 'function') return code;
    const part = formatter
      .formatToParts(2)
      .find((candidate) => candidate.type === 'currency');
    return part?.value.trim() || code;
  } catch {
    return code;
  }
}

/** The admin's symbol of a currency («$», «€», «Bs.»); unknown codes show the code. */
export function currencySymbol(code: string): string {
  return CURRENCIES.find((currency) => currency.value === code)?.symbol ?? code;
}

/**
 * «$ 8.000», «€ 12,50»: symbol first (like the rest of the admin), a space,
 * then the number in the country's format with 0 or 2 decimals.
 */
export function formatMoney(
  value: number,
  currency: string,
  country: string
): string {
  const decimals = value % 1 === 0 ? 0 : 2;
  const number = new Intl.NumberFormat(countryLocale(country), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: 2,
  }).format(value);
  // A no-break space: the symbol never ends a line apart from its number.
  return `${currencySymbol(currency)}\u00A0${number}`;
}

/**
 * «12.500», «8,50»: an amount as an editable text in the country's format,
 * which `parseMoneyInput` reads back to the same value.
 */
export function formatMoneyInput(value: number, country: string): string {
  const decimals = value % 1 === 0 ? 0 : 2;
  return new Intl.NumberFormat(countryLocale(country), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: 2,
  }).format(value);
}

const GROUPED_WITH_SPACES = /^\d{1,3}( \d{3})+([.,]\d{1,2})?$/;
const NUMERIC = /^\d[\d.,]*$/;

/**
 * «1.250.000» with `.` → «1250000»: the first group has 1–3 digits and every
 * other exactly 3; null otherwise (API `joinThousands`, §8.7 step 8).
 */
function joinThousands(text: string, separator: string): string | null {
  const groups = text.split(separator);
  if (!/^\d{1,3}$/.test(groups[0])) return null;
  if (!groups.slice(1).every((group) => /^\d{3}$/.test(group))) return null;
  return groups.join('');
}

/**
 * Digits with `.`/`,` separators → number, with exactly the country-independent
 * rules of the API menu price parser (§8.7 steps 7–8, `readSeparators`); null
 * when the text is not a number.
 */
function parseSeparatedNumber(text: string): number | null {
  // Intl groups some countries with no-break spaces («12 500» in es-CR).
  let s = text.replace(/[  ]/g, ' ');
  if (GROUPED_WITH_SPACES.test(s)) s = s.replace(/ /g, '');
  if (!NUMERIC.test(s)) return null;

  const dots = s.split('.').length - 1;
  const commas = s.split(',').length - 1;

  // No separator: an integer.
  if (dots === 0 && commas === 0) return Number(s);

  // Both kinds: the last one is the decimal separator, the other kind groups
  // thousands («1.234,5», «1,234.5»).
  if (dots > 0 && commas > 0) {
    const decimalIndex = Math.max(s.lastIndexOf('.'), s.lastIndexOf(','));
    const decimalSeparator = s[decimalIndex];
    const thousandsSeparator = decimalSeparator === '.' ? ',' : '.';
    const integerPart = s.slice(0, decimalIndex);
    const fraction = s.slice(decimalIndex + 1);
    if (!/^\d{1,2}$/.test(fraction)) return null;
    if (integerPart.includes(decimalSeparator)) return null;
    const digits = joinThousands(integerPart, thousandsSeparator);
    return digits === null ? null : Number(`${digits}.${fraction}`);
  }

  const separator = dots > 0 ? '.' : ',';

  // One kind repeated: thousands separators.
  if (dots + commas > 1) {
    const digits = joinThousands(s, separator);
    return digits === null ? null : Number(digits);
  }

  // One kind once: none after («1500.») → dropped; 1–2 digits → decimal;
  // 3 → thousands (decimal when the integer part is only zeros: «0,500»);
  // more than 3 → not a price.
  const [integerPart, after] = s.split(separator);
  if (after.length === 0) return Number(integerPart);
  if (after.length <= 2) return Number(`${integerPart}.${after}`);
  if (after.length === 3) {
    if (/^0+$/.test(integerPart)) return Number(`0.${after}`);
    const digits = joinThousands(s, separator);
    return digits === null ? null : Number(digits);
  }
  return null;
}

/**
 * A price the owner typed («8.000», «8,50», «1.234,5», «1,234.5») → a rounded
 * number; null when it is not a positive price (letters, 0, too large). The
 * separator rules are the same in every country (§8.7), so `country` does not
 * change the reading.
 */
export function parsePriceInput(text: string, _country: string): number | null {
  const trimmed = (text ?? '').trim();
  if (!trimmed) return null;
  const parsed = parseSeparatedNumber(trimmed);
  if (parsed === null || !Number.isFinite(parsed)) return null;
  const value = roundPrice(parsed);
  return value > 0 && value <= MAX_PRICE ? value : null;
}

/**
 * An amount the owner typed, with the same separator rules as
 * `parsePriceInput`; `allowZero` accepts 0 (the Menú page: «Si es gratis,
 * escriba 0»). null when it is not an amount (letters, negative, too large).
 */
export function parseMoneyInput(
  text: string,
  opts: { allowZero: boolean }
): number | null {
  const trimmed = (text ?? '').trim();
  if (!trimmed) return null;
  const parsed = parseSeparatedNumber(trimmed);
  if (parsed === null || !Number.isFinite(parsed)) return null;
  const value = roundPrice(parsed);
  if (value > MAX_PRICE) return null;
  return value > 0 || (opts.allowZero && value === 0) ? value : null;
}
